import { AppErrorBoundary } from '@/components/app-error-boundary';
import type {
  CopilotSession,
  CopilotStatefulMessage,
  CopilotStatefulToolCall,
  CopilotToolCallStatus,
} from '@customerio/hydra-copilot';
import { isToolCallFailed, ORPHANED_TOOL_CALL_ERROR } from '@customerio/hydra-copilot';
import { useCopilotAvailableTools } from '@customerio/hydra-copilot/react';
import { Box, Button, Text } from '@customerio/pluma-components/react';
import React, { useState } from 'react';
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtSource,
  ChainOfThoughtSources,
  ChainOfThoughtStep,
  type StepStatus,
} from '../primitives/chain-of-thought';
import { extractFilename } from '../../helpers/file-utils';
import { dedupeReferences } from '../../utils/references';
import { getToolCallHeaderLabel, getToolCallLabel } from '../../utils/tool-call-labels';
import { ImpactApprovalCard } from '../../tools/approval/impact-approval-card';
import { capabilityStatuses } from '../../utils/capability-status';

import { AnimateMessage } from './animate-message';

// 'unanswered' settles the card too: the user moved on without deciding, so it must
// not keep rendering as a pending approval (TAG-1886).
const SETTLED_CAPABILITY_STATUSES = new Set(['approved', 'rejected', 'denied', 'partial', 'unanswered']);

// A settled decision (or error) means the card is done, regardless of needs_approval.
function hasSettledCapabilityResponse(response: CopilotStatefulToolCall['response']): boolean {
  if (response == null) return false;
  if (typeof response.error === 'string' && response.error.length > 0) return true;
  return capabilityStatuses(response).some((status) => SETTLED_CAPABILITY_STATUSES.has(status));
}

function toolCallStepStatus(status: CopilotToolCallStatus): StepStatus {
  if (status === 'executing') return 'active';
  if (status === 'error') return 'error';
  return 'complete';
}

function combinedStatus(toolCalls: CopilotStatefulToolCall[]): CopilotToolCallStatus {
  if (toolCalls.some((toolCall) => toolCall.status === 'executing')) return 'executing';
  return 'success';
}

// The last call decides the group. Deliberately no attempt to spot a recovery: nothing
// ties a later call to an earlier failure, so treating one as cancelling the other would
// be a guess presented as fact (TAG-1886).
function endedOnFailure(toolCalls: CopilotStatefulToolCall[]): boolean {
  const last = toolCalls[toolCalls.length - 1];
  return last != null && isToolCallFailed(last);
}

function buildHeaderLabel(toolCalls: CopilotStatefulToolCall[]): string {
  if (toolCalls.length === 0) return '';

  // A failure outranks the last call: a group whose failure isn't last must not summarise
  // itself with the label of a step that succeeded (TAG-1886).
  const representative =
    toolCalls.find((toolCall) => toolCall.status === 'executing') ??
    toolCalls.find((toolCall) => toolCall.status === 'error') ??
    toolCalls.find((toolCall) => isToolCallFailed(toolCall)) ??
    toolCalls[toolCalls.length - 1];

  const label = getToolCallHeaderLabel(representative);
  const suffix = toolCalls.length > 1 ? ` (+${toolCalls.length - 1} more)` : '';
  return label + suffix;
}

// Auto mode decides server-side; surface its verdict so the user can audit the call.
function getAutoDecision(toolCall: CopilotStatefulToolCall): { approved: boolean; reasoning: string } | null {
  if (toolCall.name !== 'request_capability') {
    return null;
  }

  const raw = toolCall.response?.result;
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const result = raw as Record<string, unknown>;
  if (result.decided_by !== 'auto') {
    return null;
  }

  return {
    approved: result.status === 'approved',
    reasoning: typeof result.reasoning === 'string' ? result.reasoning : '',
  };
}

type ToolCallStepProps = { toolCall: CopilotStatefulToolCall };

function ToolCallStep({ toolCall }: ToolCallStepProps) {
  const references = toolCall.response?.result?.references;
  const uniqueReferences = dedupeReferences(references);
  const isFileOp = toolCall.name === 'read' || toolCall.name === 'write';
  const rawPath = typeof toolCall.args?.path === 'string' ? toolCall.args.path : undefined;
  const path = rawPath != null ? extractFilename(rawPath) : undefined;
  // A failed step deliberately shows none: ToolResult.Error is written for the model —
  // the reconcile messages instruct it how to address the user — so rendering it leaks
  // prompt text (TAG-1886). ORPHANED_TOOL_CALL_ERROR is the same, produced locally.
  const serverError = typeof toolCall.response?.error === 'string' ? toolCall.response.error : undefined;
  const errorMessage =
    toolCall.status === 'error' && serverError != null && serverError !== ORPHANED_TOOL_CALL_ERROR
      ? serverError
      : undefined;

  // Reason becomes expandable detail under the settled step; the label itself only shows the scope.
  const capabilityReason =
    toolCall.name === 'request_capability' &&
    toolCall.approval_request?.kind === 'capability' &&
    typeof toolCall.approval_request.reason === 'string' &&
    toolCall.approval_request.reason.length > 0
      ? toolCall.approval_request.reason
      : null;
  const autoDecision = getAutoDecision(toolCall);

  return (
    <ChainOfThoughtStep
      label={getToolCallLabel(toolCall)}
      status={toolCallStepStatus(toolCall.status)}
      hasFailure={isToolCallFailed(toolCall)}
      description={errorMessage}
      expandable={capabilityReason != null || autoDecision != null}
      badge={isFileOp && path != null ? <ChainOfThoughtSource>{path}</ChainOfThoughtSource> : undefined}
    >
      {toolCall.name === 'search_docs' && uniqueReferences.length > 0 ? (
        <ChainOfThoughtSources>
          {uniqueReferences.map((reference) => (
            <ChainOfThoughtSource key={reference.url ?? reference.title} href={reference.url}>
              {reference.title}
            </ChainOfThoughtSource>
          ))}
        </ChainOfThoughtSources>
      ) : null}
      {capabilityReason != null || autoDecision != null ? (
        <Box px="100" py="050">
          {capabilityReason}
          {autoDecision != null ? (
            <Box pt={capabilityReason != null ? '050' : undefined}>
              <Text size="xs" color="subtle">
                {autoDecision.approved ? 'Auto-approved' : 'Auto-denied'}
                {autoDecision.reasoning.length > 0 ? `: ${autoDecision.reasoning}` : ''}
              </Text>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </ChainOfThoughtStep>
  );
}

type StandardToolCallSegmentProps = {
  toolCalls: CopilotStatefulToolCall[];
  session: CopilotSession;
};

function StandardToolCallSegment({ toolCalls, session }: StandardToolCallSegmentProps) {
  // After a decision settles, drop the card so it falls back to a chain-of-thought step.
  const approvalCardIds = new Set(
    toolCalls
      .filter(
        (tc) =>
          tc.needs_approval === true &&
          tc.approval_request?.kind === 'capability' &&
          Array.isArray(tc.approval_request.scopes) &&
          !hasSettledCapabilityResponse(tc.response),
      )
      .map((tc) => tc.id),
  );
  const approvalCards = toolCalls.filter((tc) => approvalCardIds.has(tc.id));

  // Hide card-rendering tcs from chain-of-thought so the count and spinner don't include them.
  const chainOfThoughtToolCalls = toolCalls.filter((tc) => !approvalCardIds.has(tc.id));
  const status = combinedStatus(chainOfThoughtToolCalls);
  const headerStatus: StepStatus = endedOnFailure(chainOfThoughtToolCalls) ? 'error' : toolCallStepStatus(status);
  const headerLabel = buildHeaderLabel(chainOfThoughtToolCalls);

  return (
    <AnimateMessage>
      {chainOfThoughtToolCalls.length > 0 ? (
        <ChainOfThought isStreaming={status === 'executing'}>
          <ChainOfThoughtHeader status={headerStatus}>{headerLabel}</ChainOfThoughtHeader>
          <ChainOfThoughtContent>
            {chainOfThoughtToolCalls.map((toolCall) => (
              <ToolCallStep key={toolCall.id} toolCall={toolCall} />
            ))}
          </ChainOfThoughtContent>
        </ChainOfThought>
      ) : null}
      {approvalCards.map((tc) => (
        <ImpactApprovalCard key={tc.id} toolCall={tc} session={session} />
      ))}
    </AnimateMessage>
  );
}

/**
 * Fallback UI for a crashed tool render. Extracted into its own component
 * so it can use `useState` to guard the Continue button against double-clicks.
 */

type ToolCallErrorFallbackProps = { toolCall: CopilotStatefulToolCall; error: Error };

function ToolCallErrorFallback({ toolCall, error }: ToolCallErrorFallbackProps) {
  const [continued, setContinued] = useState(false);
  const canContinue = toolCall.status === 'executing' && toolCall.respond != null && !continued;

  return (
    <ChainOfThought>
      <ChainOfThoughtStep
        label={toolCall.name}
        status="error"
        description={`Something went wrong rendering this tool: ${error.message}`}
      />
      {canContinue ? (
        <Box px="100" pb="100">
          <Button
            variant="subtle"
            size="sm"
            onClick={() => {
              setContinued(true);
              toolCall.respond?.('The tool UI crashed and could not be displayed. Please continue without this tool.');
            }}
          >
            Continue
          </Button>
        </Box>
      ) : null}
    </ChainOfThought>
  );
}

/**
 * Error boundary for custom tool renders. When a render crash occurs,
 * shows a degraded ChainOfThought step with a Continue button for
 * human-in-the-loop tools so the conversation isn't permanently stuck.
 */

type ToolCallErrorBoundaryProps = {
  toolCall: CopilotStatefulToolCall;
  children: React.ReactNode;
};

function ToolCallErrorBoundary({ toolCall, children }: ToolCallErrorBoundaryProps) {
  return (
    <AppErrorBoundary fallbackRender={({ error }) => <ToolCallErrorFallback toolCall={toolCall} error={error} />}>
      {children}
    </AppErrorBoundary>
  );
}

type ToolCallsProps = {
  message: CopilotStatefulMessage & { type: 'tool_call' };
  session: CopilotSession;
};

export function ToolCalls({ message, session }: ToolCallsProps) {
  const tools = useCopilotAvailableTools(session);

  const resolveToolRender = (toolCall: (typeof message.tool_calls)[0]) => {
    // Capability approvals render as an ImpactApprovalCard at message level; skip custom render.
    if (toolCall.needs_approval === true && toolCall.approval_request != null) {
      return undefined;
    }

    return tools[toolCall.name]?.render;
  };
  // Build ordered segments that preserve the original tool call order.
  // Consecutive non-custom tools are grouped into a single collapsible block;
  // custom-rendered tools are rendered individually in place.
  type CustomSegment = { type: 'custom'; toolCall: (typeof message.tool_calls)[0] };
  type StandardSegment = { type: 'standard'; toolCalls: (typeof message.tool_calls)[0][] };
  type Segment = CustomSegment | StandardSegment;

  const segments = message.tool_calls.reduce<Segment[]>((accumulator, toolCall) => {
    const hasCustomRender = resolveToolRender(toolCall) != null;

    if (hasCustomRender) {
      return [...accumulator, { type: 'custom', toolCall }];
    }

    const lastSegment = accumulator[accumulator.length - 1];
    if (lastSegment != null && lastSegment.type === 'standard') {
      return [...accumulator.slice(0, -1), { ...lastSegment, toolCalls: [...lastSegment.toolCalls, toolCall] }];
    }

    return [...accumulator, { type: 'standard', toolCalls: [toolCall] }];
  }, []);

  return (
    <>
      {segments.map((segment, segmentIndex) => {
        if (segment.type === 'custom') {
          const render = resolveToolRender(segment.toolCall);

          if (render == null) {
            return (
              <AnimateMessage key={segmentIndex}>
                <ChainOfThought>
                  <ChainOfThoughtStep label={segment.toolCall.name} status="error" />
                </ChainOfThought>
              </AnimateMessage>
            );
          }

          return (
            <AnimateMessage key={segmentIndex}>
              <ToolCallErrorBoundary toolCall={segment.toolCall}>
                <Box>{render({ ...segment.toolCall, session })}</Box>
              </ToolCallErrorBoundary>
            </AnimateMessage>
          );
        }

        return <StandardToolCallSegment key={segmentIndex} toolCalls={segment.toolCalls} session={session} />;
      })}
    </>
  );
}
